import { AccountInfo } from "@azure/msal-browser";
import isEmail from "validator/lib/isEmail";
import isNumeric from "validator/lib/isNumeric";

/**
 * User profile information (used throughout the app).
 */
export type StaffProfile = {
	storeLocation: string;
	storeNumber: number;
	officeLocation: string;
	displayName: string;
	givenName: string;
	surName: string;
	staffId: string;
	roles: string[];
};

/**
 * Fetch user profile information from Microsoft Graph.
 */
export const getUserProfileAsync = async (account: AccountInfo, accessToken: string): Promise<StaffProfile> => {
	const graphProfileEndpoint = "https://graph.microsoft.com/v1.0/me";
	const profileFields = [
		"displayName",
		"givenName",
		"surName",
		"jobTitle",
		"officeLocation",
		"employeeId",
		"onPremisesExtensionAttributes",
		"usageLocation",
		"country",
		"mail",
	];

	const jbProfileRequestEndpoint = graphProfileEndpoint + "?$select=" + profileFields.join(",");
	const graphProfile = await fetchAuthenticatedUrl(jbProfileRequestEndpoint, accessToken);

	const staffProfile: StaffProfile = {
		staffId: getStaffId(graphProfile),
		storeLocation: "",
		storeNumber: 0,
		officeLocation: graphProfile.officeLocation,
		displayName: graphProfile.displayName,
		givenName: graphProfile.givenName,
		surName: graphProfile.surname,
		roles: getRoles(account),
	};

	if (graphProfile.onPremisesExtensionAttributes && graphProfile.onPremisesExtensionAttributes.extensionAttribute9) {
		const countryAndStoreNumber = graphProfile.onPremisesExtensionAttributes.extensionAttribute9; // in the format "AU10" for JB; "10" for TGG

		return {
			...staffProfile,
			storeLocation: countryAndStoreNumber.match(/^([A-Za-z]+)/) ? countryAndStoreNumber.match(/^([A-Za-z]+)/)[0] : "",
			storeNumber: parseInt(countryAndStoreNumber.match(/([0-9]+)$/)[0]),
		};
	}

	return staffProfile;
};

/**
 * Fetch user photo from Microsoft Graph.
 */
export const getUserPhotoAsync = async (accessToken: string): Promise<string | null> => {
	const graphProfileEndpoint = "https://graph.microsoft.com/v1.0/me/photo/$value";
	const photoBinary = await fetchPhoto(graphProfileEndpoint, accessToken);

	if (!photoBinary) return null;

	// Convert ArrayBuffer to base64 using browser-native APIs
	const bytes = new Uint8Array(photoBinary);
	let binary = "";
	for (let i = 0; i < bytes.byteLength; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
};

// ---- helpers ----

type IdTokenClaims = {
	roles?: string[];
};

/**
 * Extract app-specific roles from the account's ID token claims.
 * This matches the original implementation to maintain backward compatibility.
 */
const getRoles = (account?: AccountInfo): string[] => {
	if (account === undefined || account.idTokenClaims === undefined) {
		return [];
	}

	const idTokenClaims = account.idTokenClaims as IdTokenClaims;

	return idTokenClaims.roles ?? [];
};

const fetchAuthenticatedUrl = async (url: string, accessToken: string): Promise<any> => {
	const response = await fetch(url, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	});
	return response.json();
};

const getStaffId = (graphProfile: any): any => {
	const extensionAttribute15 = graphProfile.onPremisesExtensionAttributes?.extensionAttribute15;
	const { employeeId } = graphProfile;

	const staffIdFromEmployeeId = extensionAttribute15
		? extensionAttribute15
		: employeeId
			? parseInt(employeeId, 10).toString()
			: null;

	const staffIdFromEmail = getStaffIdFromEmail(graphProfile.mail);

	if (!(staffIdFromEmail || staffIdFromEmployeeId)) {
		throw new Error("User does not have email and employeeId");
	} else {
		return staffIdFromEmployeeId ?? staffIdFromEmail;
	}
};

const getStaffIdFromEmail = (email: string): string | null => {
	if (!(email && isEmail(email))) {
		return null;
	}
	const emailPrefix = email.substring(0, email.indexOf("@"));
	if (!isNumeric(emailPrefix)) {
		return null;
	}
	return emailPrefix;
};

const fetchPhoto = async (url: string, accessToken: string): Promise<ArrayBuffer | null> => {
	const response = await fetch(url, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	});

	if (response.status === 200) {
		return response.arrayBuffer();
	} else {
		return null;
	}
};
